/* eslint-disable import/no-cycle */
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {observer} from 'mobx-react-lite';
import {Button, Col, Form, Modal as AntModal, Row, Space} from 'antd';
import {ClockCircleOutlined} from '@ant-design/icons';
import moment from 'moment';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faPauseCircle} from '@fortawesome/free-solid-svg-icons';
import Modal from '../shared/modal/Modal';
import styles from './StateTransitionWidgetDetailModal.module.css';
import {DatePicker} from '../shared/DatePicker';
import {datetimeFormat, detailedDurationFormat} from '../../config/moment';
import InspectionTaskPendingTable from '../inspectionTask/InspectionTaskPendingTable';
import {useStore} from '../../hooks/useStore';
// eslint-disable-next-line max-len
import InspectionTaskExecutionModal from '../inspectionTask/execution/InspectionTaskExecutionModal';
import {InspectionTaskPendingStore} from '../../stores/inspectionTaskPendingStore';
import {InspectionTaskPendingWidgetConfig} from '../inspectionTask/InspectionTaskPendingWidgetConfig';
import InspectionTaskPendingWidget from '../inspectionTask/InspectionTaskPendingWidget';
import {formatDuration} from '../../utils/formatting';
import Spinner from '../shared/spinners/Spinner';
import {getIcons} from '../inspectionTask/getIcons';
import useInspectionTaskExecutionModal from '../../hooks/useInspectionTaskExecutionModal';
import {ApiError} from '../../models/apiError';

const {Item, useForm} = Form;

const PendingTasksList = observer(({relevantTaskIds, selectPendingTask}) => {
  const {t} = useTranslation();
  const store = useStore();
  const widgetConfig = new InspectionTaskPendingWidgetConfig(store, InspectionTaskPendingWidget.identifier);
  const roleIds = store.authStore.user?.roleIds;

  const configColumns = widgetConfig.getWidgetTableColumns().map((column) => {
    if (column.key === 'dueDate') {
      return {
        ...column,
        render: (value, pendingTask) => {
          if (pendingTask.pausedSince) {
            return (
              <Space>
                <FontAwesomeIcon icon={faPauseCircle}/>
                {t('inspectionTaskPendingWidget.paused')}
              </Space>
            );
          }
          const duration = moment.duration(moment(pendingTask.dueDate).diff(moment()));
          const dueDateIsBeforeNow = moment(pendingTask.dueDate).isBefore(moment());
          return (
            <span className={dueDateIsBeforeNow ? styles.redText : null}>
              {formatDuration(duration, detailedDurationFormat)}
            </span>
          );
        },
      };
    }
    return column;
  });

  const columns = [
    {
      title: '',
      key: 'icons',
      render: (value, pendingTask) => (
        <Space>
          {
            pendingTask.inspectionLot === undefined
              ? <Spinner size={'small'}/>
              : getIcons(pendingTask, roleIds)
                .map(({icon, style}) => <FontAwesomeIcon key={icon.iconName} icon={icon} size="1x" style={style}/>)
          }
        </Space>
      ),
    },
    ...configColumns,
  ];

  const datasource = InspectionTaskPendingStore.groupTasks(
    store.inspectionTaskPendingStore.getByIds(relevantTaskIds)
  );

  return (
    <>
      {t('transition.customActionError.checksBlockingStateTransition')}
      <InspectionTaskPendingTable
        style={{marginTop: '1em'}}
        columns={columns}
        dataSource={datasource}
        onRowClick={selectPendingTask}
      />
    </>
  );
});

export const StateTransitionWidgetDetailModal = ({
  transitionId,
  hasMultipleTransitions,
  lastStateChange,
  startingOperationId = undefined,
  isForceStarted = undefined,
  afterExecuteTransition = undefined,
  afterSuccessfulTransition = undefined,
  onApiError = undefined,
  ...rest
}) => {
  const {t} = useTranslation();
  const store = useStore();

  const [form] = useForm();
  const [fields] = useState([{name: ['datetime'], value: moment()}]);
  const [touched, setTouched] = useState(false);
  const [isTransitionRunning, setIsTransitionRunning] = useState(false);
  const [relevantTaskIds, setRelevantTaskIds] = useState([]);
  const {open: selectPendingTask, inspectionTaskExecutionModalProps} = useInspectionTaskExecutionModal();
  const errorModal = AntModal;

  const getPendingInspectionTasksContent = async (payload) => {
    await store.inspectionTaskPendingStore.loadByWorkplace(store.workplaceStore.selectedWorkplace.id);
    setRelevantTaskIds(payload.inspectionTaskPendingIds);
    return <PendingTasksList relevantTaskIds={payload.inspectionTaskPendingIds} selectPendingTask={selectPendingTask}/>;
  };

  const handleCustomActionErrors = async (errors) => {
    if (errors.length > 0) {
      const [error, ...errorRest] = errors;

      let title = t(`transition.customActionError.${error.type}`);

      if (!error.isTransitionAllowed) {
        title = t('transition.customActionError.stateTransitionNotAllowed');
      }

      let content = error.message;
      let width;
      if (error.additionalPayload?.inspectionTaskPendingIds) {
        content = await getPendingInspectionTasksContent(error.additionalPayload);
        width = '70vw';
      }

      errorModal[error.type]({
        title,
        content,
        width,
        onOk: () => {
          handleCustomActionErrors(errorRest);
        },
      });
    }
  };

  const executeTransition = async (values) => {
    const response = await store.transitionStore.performTransition({
      workplaceId: store.workplaceStore.selectedWorkplace?.id,
      body: {
        transitionId,
        timestamp: values.datetime ? values.datetime.seconds(0).milliseconds(0).toISOString() : undefined,
        activeOperationId: store.operationStore.active?.id,
        startingOperationId,
        isForceStarted,
      },
    }).catch((error) => {
      AntModal.error({
        title: t('transition.transitionExecute.errorOnExecute'),
        content: error.message,
      });
      return error;
    });
    if (response instanceof ApiError) {
      onApiError(response);
    } else {
      if (startingOperationId) {
        store.operationStore.load(startingOperationId);
      }

      if (store.operationStore.active?.id) {
        store.operationStore.load(store.operationStore.active?.id);
      }

      if (afterExecuteTransition) {
        afterExecuteTransition(response);
      }
    }

    if (!response?.errors?.length) {
      return true;
    }
    await handleCustomActionErrors(response.errors);

    return !(response.customActionType && response.customActionType === 'PRE');
  };

  const resetForm = () => {
    setTouched(false);
    form.resetFields();
    rest.onCancel();
  };

  const sendForm = async (values) => {
    setIsTransitionRunning(true);
    // Only send a timestamp if user interacted with the date picker.
    const result = await executeTransition(touched ? values : {});
    setIsTransitionRunning(false);

    if (result) {
      resetForm();
      if (afterSuccessfulTransition) afterSuccessfulTransition();
    }
  };

  const setDateTime = () => {
    setTouched(true);
    form.setFieldsValue({
      datetime: moment(lastStateChange.start).add(1, 'minute'),
    });
  };

  const onDateChange = () => {
    setTouched(true);
  };

  const disabledDate = (current) => {
    if (!lastStateChange) {
      return false;
    }
    const maxStart = moment(lastStateChange.start).add(1, 'minute');
    const maxEnd = moment();
    return !(maxStart.isSameOrBefore(current) && maxEnd.isSameOrAfter(current));
  };

  return (
    <>
      <Modal visible footer={false} maskClosable={false} {...rest}>
        <Form
          form={form}
          fields={fields}
          onFinish={sendForm}
          layout="vertical"
        >
          <p>{t('stateTransitionWidget.modal.form.datetime.label')}</p>
          <Row gutter={16}>
            <Col>
              <Item name="datetime" initialValue={moment()}>
                <DatePicker
                  showToday
                  showTime
                  format={datetimeFormat}
                  onChange={onDateChange}
                  disabledDate={disabledDate}
                  disabled={isTransitionRunning}
                />
              </Item>
            </Col>
            <Col>
              <Item>
                <Button onClick={setDateTime} disabled={!lastStateChange || isTransitionRunning}>
                  <ClockCircleOutlined/>
                  {t('stateTransitionWidget.modal.form.button.lastStateChange')}
                </Button>
              </Item>
            </Col>
          </Row>
          <Item className={styles.buttonRow}>
            <Button
              className={styles.buttonDiscard}
              htmlType="button"
              size="large"
              onClick={resetForm}
              danger
              disabled={isTransitionRunning}
            >
              {t('stateTransitionWidget.modal.form.button.discard')}
            </Button>
            <Button type="primary" htmlType="submit" size="large" loading={isTransitionRunning}>
              {t('stateTransitionWidget.modal.form.button.submit')}
            </Button>
          </Item>
        </Form>
      </Modal>
      <InspectionTaskExecutionModal
        {...inspectionTaskExecutionModalProps}
        onExecuted={() => {
          const stillPending = InspectionTaskPendingStore.groupTasks(
            store.inspectionTaskPendingStore.getByIds(relevantTaskIds)
          );
          if (stillPending.length === 0) {
            errorModal.destroyAll();
          }
        }}
      />
    </>
  );
};
